/**
 * ULTRA-PERFORMANCE: Virtual Data Grid
 * Navigation fluide dans 100M+ lignes
 * 
 * Features:
 * - Virtual scrolling (affiche seulement 50 lignes)
 * - Pagination intelligente
 * - Préchargement prédictif
 * - Search instantané
 * - Export chunks
 * - Zero lag même sur 100M rows
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Download, ArrowLeft, ArrowRight, Zap, Loader2 } from 'lucide-react';

import { getDuckDBEngine, DatasetMetadata } from '../../lib/duckdb-engine';
import { getBigDataBackendService } from '../../lib/big-data-backend';
import { debugLog } from '../../utils/debug-logger';
import logger from '../../utils/logger';
import AINavigatorController from './AINavigatorController';

interface VirtualDataGridProps {
  dataset: DatasetMetadata & { 
    isBackend?: boolean; 
    backendJobId?: string;
    status?: 'processing' | 'completed' | 'error';
  };
  isBackend?: boolean;
  backendJobId?: string;
  pageSize?: number;
}

// ✅ MEMO pour éviter les re-renders inutiles
const VirtualDataGrid: React.FC<VirtualDataGridProps> = React.memo(({
  dataset,
  isBackend = false,
  backendJobId,
  pageSize = 1000
}) => {
  // Component render (logs disabled)
  
  // ⚠️ IMPORTANT: Tous les hooks DOIVENT être déclarés AVANT tout return conditionnel
  // (React Rules of Hooks)
  
  // ✅ AI Navigator state (internal)
  const [aiNavigatorActive, setAiNavigatorActive] = useState(false);
  const [aiCurrentRow, setAiCurrentRow] = useState(0);
  const [aiCurrentPage, setAiCurrentPage] = useState(0);
  const [aiDiscoveries, setAiDiscoveries] = useState<Array<{
    type: 'pattern' | 'anomaly' | 'insight';
    row: number;
    page: number;
    description: string;
  }>>([]);
  
  // State hooks
  const [currentPage, setCurrentPage] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [highlightedCells, setHighlightedCells] = useState<Array<{row: number, col: string}>>([]);
  
  const cacheRef = useRef<Map<number, any[]>>(new Map());
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // ✅ Calculer les valeurs dérivées AVANT les useEffects
  const columns = dataset?.columns || [];
  const totalRows = dataset?.rowCount || 0;
  const sessionId = dataset?.id || '';
  const totalPages = Math.ceil(totalRows / pageSize);

  // ✅ SUIVRE L'IA - Changer de page automatiquement quand l'IA avance
  useEffect(() => {
    if (aiNavigatorActive && aiCurrentPage !== undefined && aiCurrentPage !== currentPage) {
      console.log(`🔄 [VirtualDataGrid] AI Navigator moved to page ${aiCurrentPage}, switching...`);
      setCurrentPage(aiCurrentPage);
    }
  }, [aiNavigatorActive, aiCurrentPage, currentPage]);
  
  // ✅ AUTO-SCROLL vers la ligne active de l'IA + HIGHLIGHT
  useEffect(() => {
    if (!aiNavigatorActive || !tableContainerRef.current) return;
    
    const expectedPage = Math.floor(aiCurrentRow / pageSize);
    if (expectedPage !== currentPage) return; // Attendre le changement de page
    
    const rowIndexInPage = aiCurrentRow % pageSize;
    const rowElement = tableContainerRef.current.querySelector(`[data-row-index="${rowIndexInPage}"]`) as HTMLElement;
    
    if (rowElement) {
      console.log(`📍 [VirtualDataGrid] Scrolling to row ${aiCurrentRow}`);
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [aiNavigatorActive, aiCurrentRow, pageSize, currentPage]);
  
  // Autres constantes dérivées
  const ROW_HEIGHT = 40;
  const COLUMN_WIDTH = 150;
  
  // ✅ MAINTENANT on peut faire les validations et returns conditionnels
  if (!dataset) {
    logger.error('[❌ VirtualDataGrid] CRITICAL: dataset is NULL/UNDEFINED!');
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-purple-700 dark:text-gray-400">Chargement du dataset...</p>
          <p className="text-sm text-purple-600 dark:text-gray-400 mt-2">
            Initialisation en cours...
          </p>
        </div>
      </div>
    );
  }

  if (!dataset.columns || !Array.isArray(dataset.columns) || dataset.columns.length === 0) {
    logger.warn('[⚠️ VirtualDataGrid] Dataset has NO VALID COLUMNS!');
    logger.warn('[⚠️ VirtualDataGrid] Dataset status:', dataset.status);
    
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-purple-700 dark:text-gray-400">Dataset en cours de traitement...</p>
          <p className="text-sm text-purple-600 dark:text-gray-400 mt-2">
            {dataset.status === 'processing' ? 'Analyse des colonnes en cours' : 'Chargement des métadonnées...'}
          </p>
        </div>
      </div>
    );
  }
  
  debugLog('[✅ VirtualDataGrid] Dataset validation passed - columns OK');
  debugLog('[📋 VirtualDataGrid] Columns:', columns.map(c => `${c.name} (${c.type})`).join(', '));
  debugLog('[📊 VirtualDataGrid] Total rows:', totalRows, 'Total pages:', totalPages);
  debugLog('[📊 VirtualDataGrid] Current State:', {
    'data.length': data.length,
    'filteredData.length': filteredData.length,
    loading,
    currentPage,
    searchTerm,
    'cacheSize': cacheRef.current.size
  });
  
  // Fonction pour fetch les données (backend ou browser)
  const onFetchPage = useCallback(async (offset: number, limit: number): Promise<any[]> => {
    if (isBackend && backendJobId) {
      // Backend fetch
      const backendService = getBigDataBackendService();
      const result = await backendService.executeQuery(
        backendJobId,
        `SELECT * FROM dataset LIMIT ${limit} OFFSET ${offset}` // ✅ FIX: "dataset" pas "data"
      );
      return result.data || [];
    } else {
      // Browser fetch (DuckDB-WASM)
      const duckdb = getDuckDBEngine();
      const result = await duckdb.query(
        `SELECT * FROM ${sessionId} LIMIT ${limit} OFFSET ${offset}`
      );
      return result.data || [];
    }
  }, [isBackend, backendJobId, sessionId]);
  
  // Fetch une page avec cache
  const fetchPage = useCallback(async (pageNumber: number) => {
    debugLog('[🔄 VirtualDataGrid] fetchPage called - page:', pageNumber);
    
    // Check cache
    if (cacheRef.current.has(pageNumber)) {
      const cached = cacheRef.current.get(pageNumber)!;
      debugLog('[💾 VirtualDataGrid] Using cached data:', cached?.length || 0, 'rows');
      setData(cached);
      setFilteredData(cached);
      return;
    }
    
    setLoading(true);
    try {
      const offset = pageNumber * pageSize;
      debugLog('[📡 VirtualDataGrid] Fetching from backend - offset:', offset, 'limit:', pageSize);
      
      const rows = await onFetchPage(offset, pageSize);
      
      debugLog('[✅ VirtualDataGrid] Received data:', rows?.length || 0, 'rows');
      if (rows && rows.length > 0) {
        debugLog('[🔍 VirtualDataGrid] First row sample:', rows[0]);
      }
      
      // Mettre en cache
      cacheRef.current.set(pageNumber, rows);
      
      // Limite cache à 10 pages (10k lignes)
      if (cacheRef.current.size > 10) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey !== undefined) {
          cacheRef.current.delete(firstKey);
        }
      }
      
      debugLog('[📊 VirtualDataGrid] Setting data state with', rows?.length || 0, 'rows');
      setData(rows);
      setFilteredData(rows);
    } catch (error) {
      logger.error('[❌ VirtualDataGrid] Failed to fetch page:', error);
    } finally {
      setLoading(false);
    }
  }, [pageSize, onFetchPage]);
  
  // Préchargement prédictif
  useEffect(() => {
    // Précharger la page suivante
    if (currentPage + 1 < totalPages) {
      const nextPage = currentPage + 1;
      if (!cacheRef.current.has(nextPage)) {
        onFetchPage(nextPage * pageSize, pageSize).then(rows => {
          cacheRef.current.set(nextPage, rows);
        });
      }
    }
  }, [currentPage, totalPages, pageSize, onFetchPage]);
  
  // Load initial page
  useEffect(() => {
    fetchPage(currentPage);
  }, [currentPage, fetchPage]);
  
  // Search filter
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = data.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(term)
      )
    );
    setFilteredData(filtered);
  }, [searchTerm, data]);
  
  // Navigation
  const goToPage = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      setSearchTerm(''); // Reset search on page change
    }
  };
  
  // Export current page
  const exportPage = () => {
    const csv = [
      columns.map(c => c.name).join(','),
      ...filteredData.map(row =>
        columns.map(c => row[c.name]).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data_page_${currentPage + 1}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-purple-50/20 to-blue-50/20 dark:from-slate-800 dark:to-slate-800 rounded-xl border border-purple-200 dark:border-slate-700 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-purple-200 dark:border-slate-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-900 dark:to-slate-900">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-purple-500" />
            <span className="font-bold text-purple-900 dark:text-white">
              {totalRows.toLocaleString()} lignes
            </span>
            <span className="text-xs text-purple-600 dark:text-gray-400">
              (page {currentPage + 1}/{totalPages})
            </span>
            <span className="text-xs text-purple-600 dark:text-gray-400">
              • {columns.length} colonnes
            </span>
          </div>
          
          {loading && (
            <div className="flex items-center space-x-2 text-blue-500">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Chargement...</span>
            </div>
          )}
          
          {!loading && filteredData.length > 0 && (
            <div className="text-xs text-green-500 flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>{filteredData.length} lignes chargées</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600 dark:text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border border-purple-200 dark:border-slate-700 rounded-lg text-sm text-purple-900 dark:text-white placeholder-purple-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          {/* Export */}
          <button
            onClick={exportPage}
            className="px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg text-sm text-purple-900 dark:text-white flex items-center space-x-2 transition-all shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>
      
      {/* Scroll Hint */}
      {columns.length > 5 && (
        <div className="px-4 py-2 bg-blue-500/10 border-b border-blue-500/30 flex items-center space-x-2 text-sm text-blue-400">
          <ArrowRight className="w-4 h-4" />
          <span>Défilez horizontalement pour voir toutes les colonnes ({columns.length} total)</span>
        </div>
      )}
      
      {/* Simple Table (Virtual scrolling with pagination) */}
      <div 
        ref={tableContainerRef}
        className="flex-1 overflow-auto" 
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        <table className="text-sm data-grid-table" style={{ minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead className="sticky top-0 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border-b-2 border-purple-200 dark:border-slate-700 z-10">
            <tr>
              {columns.map((col, colIndex) => {
                const isLongText = col.name.toLowerCase().includes('text') || 
                                  col.name.toLowerCase().includes('summary') ||
                                  col.name.toLowerCase().includes('description');
                
                return (
                  <th
                    key={col.name}
                    className="px-4 py-3 text-left font-semibold text-purple-900 dark:text-white border-r border-purple-200 dark:border-slate-700 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800"
                    style={{ 
                      minWidth: isLongText ? '300px' : '150px',
                      maxWidth: isLongText ? '500px' : '300px',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="font-bold">{col.name}</span>
                      <span className="text-xs text-purple-600 dark:text-gray-400 font-normal">({col.type})</span>
                    </div>
                    {colIndex === 0 && (
                      <div className="text-xs text-purple-600 dark:text-gray-400/70 mt-1">
                        {filteredData.length} lignes affichées
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(() => {
              debugLog('[🎨 RENDER] filteredData.length:', filteredData.length);
              debugLog('[🎨 RENDER] loading:', loading);
              debugLog('[🎨 RENDER] currentPage:', currentPage);
              if (filteredData.length > 0) {
                debugLog('[🎨 RENDER] First row:', JSON.stringify(filteredData[0]).substring(0, 200));
              }
              return null;
            })()}
            
            {loading && filteredData.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500 mb-2" />
                  <div className="text-purple-700 dark:text-gray-400">Chargement des données...</div>
                </td>
              </tr>
            )}
            
            {!loading && filteredData.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center">
                  <div className="text-purple-700 dark:text-gray-400 text-lg">
                    {searchTerm ? 'Aucune donnée trouvée pour cette recherche' : 'Aucune donnée à afficher'}
                  </div>
                  <div className="text-purple-600 dark:text-gray-400/60 text-sm mt-2">
                    Page {currentPage + 1} / {totalPages}
                  </div>
                </td>
              </tr>
            )}
            
            {filteredData.map((row, rowIndex) => {
              // 🔍 AUDIT: Log détaillé pour la première ligne
              if (rowIndex === 0) {
                debugLog('[🔍 AUDIT] First row keys:', Object.keys(row));
                debugLog('[🔍 AUDIT] First row full data:', row);
                columns.forEach(col => {
                  const rowKeys = Object.keys(row);
                  const matchingKey = rowKeys.find(k => k.toLowerCase() === col.name.toLowerCase()) || col.name;
                  const value = row[matchingKey];
                  debugLog(`[🔍 AUDIT] Column "${col.name}" -> Key "${matchingKey}" -> Value:`, 
                    value === null ? 'NULL' : 
                    value === undefined ? 'UNDEFINED' : 
                    typeof value === 'string' && value.length > 50 ? `"${value.substring(0, 50)}..." (${value.length} chars)` : 
                    value
                  );
                });
              }
              
              // ✅ CALCULER SI C'EST LA LIGNE QUE L'IA ANALYSE
              const globalRowIndex = currentPage * pageSize + rowIndex;
              const isAIAnalyzing = aiNavigatorActive && globalRowIndex === aiCurrentRow;
              
              return (
                <tr
                  key={rowIndex}
                  data-row-index={rowIndex}
                  className={`data-grid-row border-b border-purple-200 dark:border-slate-700 transition-all duration-300 ${
                    isAIAnalyzing 
                      ? 'bg-gradient-to-r from-purple-900/40 via-pink-900/40 to-purple-900/40 ring-2 ring-purple-500/50' 
                      : 'hover:bg-purple-50/50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  {columns.map((col) => {
                    // ✅ FIX: Trouver la clé correspondante dans la row (case-insensitive)
                    const rowKeys = Object.keys(row);
                    const matchingKey = rowKeys.find(k => k.toLowerCase() === col.name.toLowerCase()) || col.name;
                    const value = row[matchingKey];
                    
                    // Déterminer le style de la cellule selon le type de colonne
                    const isLongText = col.name.toLowerCase().includes('text') || 
                                      col.name.toLowerCase().includes('summary') ||
                                      col.name.toLowerCase().includes('description');
                    
                    return (
                      <td
                        key={col.name}
                        className="px-4 py-2 text-purple-900 dark:text-white border-r border-purple-200 dark:border-slate-700"
                        style={{
                          minWidth: isLongText ? '300px' : '150px',
                          maxWidth: isLongText ? '500px' : '300px',
                          whiteSpace: isLongText ? 'normal' : 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'top'
                        }}
                        title={value !== null && value !== undefined ? String(value) : ''}
                      >
                        {value !== null && value !== undefined ? String(value) : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* AI Navigator Controller - NE PLUS GÉRER ICI, c'est fait par le parent */}
      {/* (Commenté pour éviter les doublons, maintenant dans DatasetWorkspace) */}
      
      {/* Pagination */}
      <div className="flex items-center justify-between p-4 border-t border-purple-200 dark:border-slate-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800">
        <div className="text-sm text-purple-700 dark:text-gray-400">
          Affichage {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalRows)} sur {totalRows.toLocaleString()}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => goToPage(0)}
            disabled={currentPage === 0}
            className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm text-purple-900 dark:text-white"
          >
            Première
          </button>
          
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          
          {/* Page numbers uniques (max 5) */}
          {(() => {
            const pages: number[] = [];
            const start = Math.max(0, currentPage - 2);
            const end = Math.min(totalPages - 1, currentPage + 2);
            for (let p = start; p <= end; p++) pages.push(p);
            return pages;
          })().map((page) => (
            <button
              key={`page-${page}`}
              onClick={() => goToPage(page)}
              className={`px-4 py-2 border rounded-lg text-sm transition-colors ${
                page === currentPage
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent'
                  : 'bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border-purple-200 dark:border-slate-700 text-purple-900 dark:text-white'
              }`}
            >
              {page + 1}
            </button>
          ))}
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => goToPage(totalPages - 1)}
            disabled={currentPage === totalPages - 1}
            className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border border-purple-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm text-purple-900 dark:text-white"
          >
            Dernière
          </button>
        </div>
        
        <div className="text-sm text-purple-700 dark:text-gray-400">
          {filteredData.length !== data.length && (
            <span className="text-purple-500 font-semibold">
              {filteredData.length} résultats filtrés
            </span>
          )}
        </div>
      </div>

      {/* ✅ AI NAVIGATOR - Toujours affiché en fixed top-right */}
      {dataset.status === 'completed' && (
        <AINavigatorController
          jobId={backendJobId || ''}
          datasetId={dataset.id || ''}
          totalRows={totalRows}
          totalPages={totalPages}
          pageSize={pageSize}
          columns={columns}
          onNavigationStart={(isActive) => {
            console.log('📣 [VirtualDataGrid] AI Navigator started:', isActive);
            setAiNavigatorActive(isActive);
          }}
          onNavigationUpdate={(page, row) => {
            console.log('📍 [VirtualDataGrid] AI Navigator update:', { page, row });
            setAiCurrentRow(row);
            setAiCurrentPage(page);
          }}
          onDiscovery={(discovery) => {
            setAiDiscoveries(prev => [...prev, discovery as any]);
          }}
          onComplete={() => {
            console.log('✅ [VirtualDataGrid] AI Navigator completed');
            setAiNavigatorActive(false);
          }}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // ✅ Custom comparison pour éviter re-renders inutiles
  return (
    prevProps.dataset?.id === nextProps.dataset?.id &&
    prevProps.isBackend === nextProps.isBackend &&
    prevProps.backendJobId === nextProps.backendJobId &&
    prevProps.pageSize === nextProps.pageSize
  );
});

VirtualDataGrid.displayName = 'VirtualDataGrid';

export default VirtualDataGrid;

